import type { Mandate, MandateInput } from '@aimmo/contracts';
import {
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS,
  MANDATE_STATUSES,
  MANDATE_STATUS_LABELS,
  enumOptions,
  mandateCreateSchema,
} from '@aimmo/contracts';
import { Badge, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { useResourcePermissions } from '../lib/abilities.js';
import { resolveContact, searchContacts } from './contact-lookups.js';
import { resolveProperty, resolveUser, searchProperties, searchUsers } from './mandate-lookups.js';
import { MandateTransitions } from './mandate-transitions.js';
import { ActivityTimeline } from '../features/activity-timeline.js';
import { formatPrice } from '../lib/format.js';
import { MANDATE_STATUS_TONES } from './mandate-status.js';

const STATUS_OPTIONS = enumOptions(MANDATE_STATUSES, MANDATE_STATUS_LABELS);
const CONTRACT_OPTIONS = enumOptions(CONTRACT_TYPES, CONTRACT_TYPE_LABELS);

/** The lifecycle controls need to know whether this user may run them. */
function TransitionsSection({ mandate }: { mandate: Mandate }) {
  const permissions = useResourcePermissions('Mandate');
  return <MandateTransitions mandate={mandate} allowed={permissions.transition ?? false} />;
}

/**
 * Mandates (docs/domain/06-mandates.md) — the spine of the business.
 *
 * `status` appears in the list and on the detail but never in the form: it is
 * reached by a transition, and the buttons for those come from the API's own
 * reading of the state machine.
 */
export const mandatesResource = defineResource<Mandate, MandateInput, MandateInput, MandateInput>({
  key: 'mandates',
  label: { one: 'Incarico', many: 'Incarichi', gender: 'm' },
  client: tsRestResourceClient(api.mandates),
  getRowId: (row) => row.id,
  defaultState: { sort: 'createdAt', order: 'desc' },
  search: { placeholder: 'Titolo, indirizzo, proprietario…' },
  columns: [
    { id: 'propertyLabel', header: 'Immobile' },
    { id: 'ownerName', header: 'Proprietario' },
    {
      id: 'contractType',
      header: 'Contratto',
      width: '8rem',
      accessor: (row) => CONTRACT_TYPE_LABELS[row.contractType],
    },
    {
      id: 'status',
      header: 'Stato',
      width: '9rem',
      cell: ({ row }) => (
        <Badge tone={MANDATE_STATUS_TONES[row.status]}>{MANDATE_STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      id: 'price',
      header: 'Prezzo',
      sortKey: 'price',
      align: 'right',
      width: '9rem',
      accessor: (row) => formatPrice(row.price, ''),
    },
    {
      id: 'exclusive',
      header: 'Esclusiva',
      width: '8rem',
      cell: ({ row }) => (row.exclusive ? <Badge tone="accent">Sì</Badge> : null),
    },
    {
      id: 'expiresAt',
      header: 'Scadenza',
      sortKey: 'expiresAt',
      width: '9rem',
      accessor: (row) => (row.expiresAt ? new Date(row.expiresAt) : null),
    },
    { id: 'agentName', header: 'Agente', hiddenByDefault: true },
  ],
  filters: [
    { id: 'status', label: 'Stato', type: 'enum', options: STATUS_OPTIONS },
    { id: 'contractType', label: 'Contratto', type: 'enum', options: CONTRACT_OPTIONS },
    {
      id: 'ownerContactId',
      label: 'Proprietario',
      type: 'relation',
      search: searchContacts,
      resolve: resolveContact,
    },
    {
      id: 'propertyId',
      label: 'Immobile',
      type: 'relation',
      search: searchProperties,
      resolve: resolveProperty,
    },
    {
      id: 'agentUserId',
      label: 'Agente',
      type: 'relation',
      search: searchUsers,
      resolve: resolveUser,
    },
    { id: 'exclusive', label: 'Esclusiva', type: 'boolean' },
    { id: 'published', label: 'Pubblicato', type: 'boolean' },
    // The renewal worklist: everything lapsing before a date.
    { id: 'expiresBefore', label: 'Scade entro', type: 'text', placeholder: '2026-12-31' },
  ],
  form: {
    schema: mandateCreateSchema,
    defaultValues: {
      propertyId: '',
      ownerContactId: '',
      agentUserId: '',
      contractType: 'sale',
      price: '',
      commissionPct: '',
      commissionAmount: '',
      exclusive: false,
      signedAt: '',
      expiresAt: '',
      published: false,
      title: '',
      description: '',
    },
    // "Crea incarico" on a property detail arrives as ?propertyId=…; anything
    // else in the query string is not this form's business.
    fromSearch: (search) => (search['propertyId'] ? { propertyId: search['propertyId'] } : {}),
    fromItem: (mandate) => {
      return {
        propertyId: mandate.propertyId,
        ownerContactId: mandate.ownerContactId,
        agentUserId: mandate.agentUserId ?? '',
        contractType: mandate.contractType,
        price: mandate.price ?? '',
        commissionPct: mandate.commissionPct ?? '',
        commissionAmount: mandate.commissionAmount ?? '',
        exclusive: mandate.exclusive,
        signedAt: mandate.signedAt ?? '',
        expiresAt: mandate.expiresAt ?? '',
        published: mandate.published,
        title: mandate.title ?? '',
        description: mandate.description ?? '',
      };
    },
    sections: [
      {
        id: 'parties',
        title: 'Parti',
        fields: [
          {
            name: 'propertyId',
            label: 'Immobile',
            type: 'relation',
            required: true,
            search: searchProperties,
            resolve: resolveProperty,
          },
          {
            name: 'ownerContactId',
            label: 'Proprietario mandante',
            type: 'relation',
            required: true,
            search: searchContacts,
            resolve: resolveContact,
          },
          {
            name: 'agentUserId',
            label: 'Agente incaricato',
            type: 'relation',
            search: searchUsers,
            resolve: resolveUser,
          },
        ],
      },
      {
        id: 'terms',
        title: 'Condizioni',
        description: 'Il prezzo qui è quello richiesto: quello indicativo vive sull’immobile.',
        fields: [
          {
            name: 'contractType',
            label: 'Tipo di contratto',
            type: 'select',
            required: true,
            options: CONTRACT_OPTIONS,
          },
          { name: 'price', label: 'Prezzo richiesto (€)', type: 'text', placeholder: '285000.00' },
          { name: 'commissionPct', label: 'Provvigione (%)', type: 'text', placeholder: '3.00' },
          { name: 'commissionAmount', label: 'Provvigione (€)', type: 'text' },
          { name: 'exclusive', label: 'Incarico in esclusiva', type: 'switch' },
          { name: 'signedAt', label: 'Firmato il', type: 'date' },
          { name: 'expiresAt', label: 'Scade il', type: 'date' },
        ],
      },
      {
        id: 'marketing',
        title: 'Pubblicazione',
        columns: 1,
        fields: [
          { name: 'published', label: 'Pubblicabile sui portali', type: 'switch' },
          { name: 'title', label: 'Titolo annuncio', type: 'text', wide: true },
          { name: 'description', label: 'Descrizione', type: 'textarea', rows: 6, wide: true },
        ],
      },
    ],
  },
  detail: {
    title: (row) => `${row.propertyLabel} · ${CONTRACT_TYPE_LABELS[row.contractType]}`,
    // The audit trail lands here: every transition writes a status_change.
    after: (row) => <ActivityTimeline subjectType="mandate" subjectId={row.id} />,
    sections: [
      {
        id: 'lifecycle',
        title: 'Stato',
        render: (row) => (
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={MANDATE_STATUS_TONES[row.status]}>
                {MANDATE_STATUS_LABELS[row.status]}
              </Badge>
              {row.exclusive && <Badge tone="accent">Esclusiva</Badge>}
              {row.published && <Badge tone="neutral">Pubblicato</Badge>}
            </div>
            <TransitionsSection mandate={row} />
          </div>
        ),
      },
      {
        id: 'terms',
        title: 'Condizioni',
        columns: 3,
        fields: [
          { id: 'ownerName', label: 'Proprietario' },
          { id: 'agentName', label: 'Agente' },
          {
            id: 'contractType',
            label: 'Contratto',
            accessor: (row) => CONTRACT_TYPE_LABELS[row.contractType],
          },
          {
            id: 'price',
            label: 'Prezzo richiesto',
            accessor: (row) => formatPrice(row.price),
          },
          {
            id: 'commissionPct',
            label: 'Provvigione',
            accessor: (row) =>
              row.commissionPct
                ? `${row.commissionPct} %`
                : row.commissionAmount
                  ? formatPrice(row.commissionAmount)
                  : '—',
          },
          {
            id: 'signedAt',
            label: 'Firmato il',
            accessor: (row) => (row.signedAt ? new Date(row.signedAt) : null),
          },
          {
            id: 'expiresAt',
            label: 'Scade il',
            accessor: (row) => (row.expiresAt ? new Date(row.expiresAt) : null),
          },
          {
            id: 'closedAt',
            label: 'Chiuso il',
            accessor: (row) => (row.closedAt ? new Date(row.closedAt) : null),
          },
          {
            id: 'closedPrice',
            label: 'Prezzo finale',
            accessor: (row) => formatPrice(row.closedPrice),
          },
        ],
      },
      {
        id: 'marketing',
        title: 'Annuncio',
        columns: 1,
        fields: [
          { id: 'title', label: 'Titolo' },
          { id: 'description', label: 'Descrizione' },
        ],
      },
    ],
  },
  empty: {
    title: 'Nessun incarico',
    description: 'Un incarico lega un immobile al proprietario che lo affida all’agenzia.',
  },
});
